import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { peopleService, Person, CreatePersonData, UpdatePersonData } from '../../services/peopleService';
import './PeopleEdit.css';

const PeopleEdit: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { user } = useAuth();
    const navigate = useNavigate();
    const isEditMode = Boolean(id);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        notes: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (isEditMode && id) {
            setLoading(true);
            peopleService.getPersonById(parseInt(id))
                .then(res => {
                    if (res.success) {
                        setFormData({
                            name: res.person.name,
                            email: res.person.email || '',
                            phone: res.person.phone || '',
                            notes: res.person.notes || ''
                        });
                    } else {
                        setError('Failed to fetch person data.');
                    }
                })
                .catch(() => setError('An error occurred.'))
                .finally(() => setLoading(false));
        }
    }, [id, isEditMode]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user) {
            setError('You must be logged in.');
            return;
        }
        if (!formData.name) {
            setError('Name is required.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            if (isEditMode && id) {
                const updateData: UpdatePersonData = { ...formData };
                await peopleService.updatePerson(parseInt(id), updateData);
                navigate(`/people/${id}`);
            } else {
                const createData: CreatePersonData = { ...formData, user_id: user.id };
                const response = await peopleService.createPerson(createData);
                navigate(`/people/${response.id}`);
            }
        } catch (err) {
            setError('Failed to save person.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="people-edit-container">
            <h1>{isEditMode ? 'Edit Person' : 'Add New Person'}</h1>
            {loading && <p>Loading...</p>}
            {error && <p className="error-message">{error}</p>}
            <form onSubmit={handleSubmit} className="people-edit-form">
                <div className="form-group">
                    <label htmlFor="name">Name *</label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="phone">Phone</label>
                    <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="notes">General Notes</label>
                    <textarea
                        id="notes"
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        rows={6}
                    />
                </div>
                <div className="form-actions">
                    <button type="button" onClick={() => navigate(isEditMode ? `/people/${id}` : '/people')} className="btn">
                        Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                        {loading ? 'Saving...' : 'Save Person'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default PeopleEdit;
